import { observer } from "mobx-react-lite";
import { useState, useEffect } from "react";
import { HeadsUpSidebar } from "@/components/heads-up";
import { MemItSidePanelView } from "@/components/mem-it";
import styled from "@emotion/styled";
import { motion } from "motion/react";
import { useAppStore } from "@/store";
import { SidePanelRouteType, SidePanelTab } from "@/store/routing/types";
import { SIDE_PANEL_COLLAPSED_HEIGHT_PX, OPEN_TOP_GAP_PX } from "@/app/router/constants";
import { ZIndex } from "@/domains/design/constants";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

export const SidePanelRouter = observer(function SidePanelRouter() {
  const { pageStore, store } = useAppStore();
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);

  const memItStore = pageStore.memItPage;
  const sidePanelStore = store.sidePanel;
  const storeSessionId = memItStore.session?.id;
  const activeRoute = sidePanelStore.activeRoute;
  const activeTabId = sidePanelStore.activeTabId;
  const isChatOpen = activeTabId === SidePanelTab.MEM_IT;
  const routeSessionId =
    activeRoute.type === SidePanelRouteType.MEM_IT ? activeRoute.sessionId : undefined;

  useEffect(() => {
    // Track side panel view
    const hasOverlayOpen = store.platformInfo.onboardingInfo.shouldShowWelcomeOverlay;
    if (hasOverlayOpen) return;

    trackEvent(TrackedEvent.SidePanelView);
  }, [store.platformInfo.onboardingInfo.shouldShowWelcomeOverlay]);

  useEffect(() => {
    // Prefer the side panel route's sessionId when available
    if (routeSessionId) {
      setSessionId(routeSessionId);
      return;
    }

    // Fall back to the current session in the store if available
    if (storeSessionId) {
      setSessionId(storeSessionId);
      return;
    }

    // Try to resume an active session
    // Otherwise create or get a placeholder session id
    memItStore
      .resumeActiveSession()
      .then(resumedSessionId => {
        if (resumedSessionId) {
          setSessionId(resumedSessionId);
          return;
        }
        // No active session found, get a placeholder session id
        return memItStore.getPlaceholderSessionId();
      })
      .then(id => {
        if (id) {
          setSessionId(id);
        }
      })
      .catch(error => {
        logger.error({
          message: "Error getting session id in SidePanelRouter",
          info: {
            error: objectModule.safeErrorAsJson(error as Error),
          },
        });
      });
  }, [memItStore, routeSessionId, storeSessionId]);

  // Reset animation skip flag after first render
  useEffect(() => {
    if (sidePanelStore.shouldSkipAnimation && sidePanelStore.isInitialized) {
      // Reset the flag animation skip once we render
      const timeoutId = setTimeout(() => {
        sidePanelStore.resetShouldSkipAnimation();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [sidePanelStore.isInitialized, sidePanelStore.shouldSkipAnimation, sidePanelStore]);

  return (
    <Container>
      {sidePanelStore.isInitialized && (
        <HeadsUpContainer>
          <HeadsUpSidebar />
        </HeadsUpContainer>
      )}

      <MemItOverlay
        initial={false}
        animate={{
          top: isChatOpen ? OPEN_TOP_GAP_PX : `calc(100% - ${SIDE_PANEL_COLLAPSED_HEIGHT_PX}px)`,
        }}
        transition={
          sidePanelStore.shouldSkipAnimation
            ? { duration: 0 }
            : { type: "spring", bounce: 1, mass: 0.17, stiffness: 200 }
        }
      >
        <MemItContainer
          onClick={() => {
            if (!isChatOpen) {
              sidePanelStore.goToMemIt(sessionId);
            }
          }}
        >
          <MemItSidePanelView
            sessionId={sessionId}
            isClosed={!isChatOpen}
            onClose={sidePanelStore.goToHeadsUp}
          />
        </MemItContainer>
      </MemItOverlay>
    </Container>
  );
});

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const HeadsUpContainer = styled.div`
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
`;

const MemItOverlay = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${ZIndex.SidebarMemItOverlay};
  display: flex;
  flex-direction: column;
  will-change: transform;
`;

const MemItContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;
